import { audioStreamUrl } from '@/api/endpoints/media'
import { formatDuration } from '@/lib/format'
import styles from './NarrationDock.module.scss'

export interface NarrationDockProps {
  audioPath: string | null | undefined
  durationSec?: number | null
  language: string
}

/**
 * Lightweight player for the TTS narration. We use the native ``<audio>``
 * element — its built-in transport bar already handles play/pause/seek/
 * volume/keyboard, and the backend serves the file with ``Accept-Ranges``
 * so seeking works without downloading the whole mp3 first. A waveform
 * scrubber would be nice but adds noticeable complexity for marginal UX
 * gain in a single-narration playback.
 */
export function NarrationDock({ audioPath, durationSec, language }: NarrationDockProps) {
  const src = audioStreamUrl(audioPath)
  if (!src) {
    return (
      <div className={styles.empty}>
        Sem narração salva. Aparece aqui quando o pipeline terminar a etapa de TTS.
      </div>
    )
  }

  const filename = audioPath ? audioPath.split('/').pop() ?? audioPath : ''

  return (
    <div className={styles.shell}>
      {/* Captions are surfaced in the dedicated "Legendas" tab; mounting an
          empty <track> here adds noise without improving accessibility for
          this single-language TTS playback. */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio className={styles.player} controls preload="metadata" src={src} />
      <div className={styles.meta}>
        <div className={styles.metaRow}>
          <span className={styles.metaKey}>Arquivo</span>
          <span className={styles.metaValue}>{filename}</span>
        </div>
        <div className={styles.metaRow}>
          <span className={styles.metaKey}>Idioma</span>
          <span className={styles.metaValue}>{language}</span>
        </div>
        {durationSec ? (
          <div className={styles.metaRow}>
            <span className={styles.metaKey}>Duração</span>
            <span className={styles.metaValue}>{formatDuration(durationSec)}</span>
          </div>
        ) : null}
      </div>
    </div>
  )
}
